import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell } from "@phosphor-icons/react";
import api from "../api";

// v415.0 — SHORTFALL ALERT BELL (header). Red-badged bell that lights up
// whenever a store order pulls stock from the user's shelf (covering
// account) or, for elevated users, from any shelf in the org. Opening the
// panel shows the history (product + units pulled) and marks all read.
export const ShortfallBell = () => {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState(null);
  const ref = useRef(null);

  const poll = useCallback(() => {
    api.get("/shortfall-alerts/unread-count")
      .then(({ data }) => setCount(data.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 45000);
    return () => clearInterval(id);
  }, [poll]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const toggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setAlerts(null);
    try {
      const { data } = await api.get("/shortfall-alerts", { params: { limit: 30 } });
      const rows = data.rows || [];
      setAlerts(rows);
      if (rows.some((a) => !a.read)) {
        await api.post("/shortfall-alerts/mark-read");
        setCount(0);
      }
    } catch {
      setAlerts([]);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        aria-label={t("shortfall_bell.aria")}
        data-testid="shortfall-bell-button"
        className="relative p-2 rounded-md text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100 transition-colors"
      >
        <Bell size={18} weight="bold" />
        {count > 0 && (
          <span
            data-testid="shortfall-bell-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center leading-none"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="shortfall-bell-panel"
          className="absolute right-0 top-full mt-2 w-[320px] sm:w-[360px] bg-white border border-zinc-200 rounded-xl shadow-xl z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-zinc-100">
            <h3 className="text-sm font-semibold text-zinc-950 flex items-center gap-2">
              <Bell size={14} weight="bold" className="text-amber-600" />
              {t("shortfall_bell.title")}
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">{t("shortfall_bell.sub")}</p>
          </div>
          <div className="max-h-[320px] overflow-y-auto divide-y divide-zinc-100">
            {alerts === null && (
              <p className="px-4 py-6 text-xs text-zinc-400 text-center">{t("common.loading")}</p>
            )}
            {alerts !== null && alerts.length === 0 && (
              <p className="px-4 py-6 text-xs text-zinc-400 text-center" data-testid="shortfall-bell-empty">
                {t("shortfall_bell.empty")}
              </p>
            )}
            {(alerts || []).map((a) => (
              <div
                key={a.id}
                data-testid="shortfall-alert-row"
                className={`px-4 py-2.5 text-[12px] ${a.read ? "bg-white" : "bg-amber-50/60"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold text-zinc-900">
                    {t("shortfall_bell.line", {
                      units: a.units,
                      product: a.product_name,
                      covering: a.covering_name,
                    })}
                  </span>
                  {!a.read && (
                    <span className="shrink-0 px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[9px] font-bold">
                      {t("shortfall_bell.new")}
                    </span>
                  )}
                </div>
                <div className="text-zinc-500 mt-0.5 flex flex-wrap items-baseline gap-x-2">
                  <span>{t("shortfall_bell.order_by", { reference: a.reference, seller: a.seller_name })}</span>
                  {a.still_short > 0 && (
                    <span className="text-red-600 font-semibold">
                      {t("shortfall_bell.still_short", { n: a.still_short })}
                    </span>
                  )}
                </div>
                <div className="font-mono text-[10px] text-zinc-400 mt-0.5">
                  {(a.created_at || "").slice(0, 16).replace("T", " ")}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
